import { Injectable } from '@angular/core';
import {
  AddChecklistItemResponse,
  AssetDetail,
  AssetItem,
  AssetListParams,
  AssetsRepository,
  AssetStatus,
  BodyAddChecklistCategory,
  BodyAddChecklistItem,
  BodyCreateAsset,
  BodyCreateAssetItem,
  BulkDeleteAssetItems,
  ChecklistCategory,
  ChecklistDetail,
  ChecklistListParams,
  ChecklistType,
  CollectionAndReturnData,
  CollectionAndReturnParams,
  CollectionReturnDetail,
  CreateAssetItemResponse,
  CreateAssetResponse,
} from '@app/domain';
import { PaginatedResponse } from '@app/shared/models/global.model';
import { map, Observable } from 'rxjs';
import { AssetsService } from '../api-clients';
import { AssetsMapper } from '../mappers';

@Injectable()
export class AssetsRepositoryImpl implements AssetsRepository {
  private readonly _assetsMapper = new AssetsMapper();

  constructor(private readonly _assetsService: AssetsService) {}

  getAssetsList(
    params: AssetListParams
  ): Observable<PaginatedResponse<AssetItem>> {
    const assetListDTO = this._assetsService.getList(params);
    return assetListDTO.pipe(
      map(dto => this._assetsMapper.assetTypeToDomain(dto))
    );
  }

  createAssetType(body: BodyCreateAsset): Observable<CreateAssetResponse> {
    const responseDto = this._assetsService.create(body);
    return responseDto.pipe(
      map(dto => this._assetsMapper.createAssetToDomain(dto))
    );
  }

  getAssetTypeDetail(
    id: number,
    param: AssetListParams
  ): Observable<AssetDetail> {
    const assetDetailDTO = this._assetsService.detail(id, param);
    return assetDetailDTO.pipe(
      map(dto => this._assetsMapper.assetTypeDetailToDomain(dto))
    );
  }

  getAssetStatus(): Observable<AssetStatus> {
    const assetStatusDTO = this._assetsService.getAssetStatus();
    return assetStatusDTO.pipe(
      map(dto => this._assetsMapper.assetStatusToDomain(dto))
    );
  }

  addItem(
    id: number | null,
    body: BodyCreateAssetItem
  ): Observable<CreateAssetItemResponse> {
    const responseDto = this._assetsService.addItem(id, body);
    return responseDto.pipe(
      map(dto => this._assetsMapper.createAssetItemToDomain(dto))
    );
  }

  bulkDeleteItems(body: BulkDeleteAssetItems): Observable<void> {
    return this._assetsService.bulkDeleteItems(body);
  }

  getChecklist(
    id: number,
    params: ChecklistListParams
  ): Observable<ChecklistType> {
    const checklistDTO = this._assetsService.getChecklist(id, params);
    return checklistDTO.pipe(
      map(dto => this._assetsMapper.assetItemChecklistToDomain(dto))
    );
  }

  getCollectReturn(
    id: number,
    params: CollectionAndReturnParams
  ): Observable<PaginatedResponse<CollectionAndReturnData>> {
    const collectionAndReturnDTO = this._assetsService.getCollectReturn(
      id,
      params
    );
    return collectionAndReturnDTO.pipe(
      map(dto => this._assetsMapper.assetCollectReturnToDomain(dto))
    );
  }

  // Category methods Start

  getCategory(assetTypeId: number): Observable<ChecklistCategory[]> {
    const categoryDTO = this._assetsService.getCategory(assetTypeId);
    return categoryDTO.pipe(
      map(dto => this._assetsMapper.assetItemCategoriesToDomain(dto))
    );
  }

  addCategory(
    assetTypeId: number,
    body: BodyAddChecklistCategory
  ): Observable<void> {
    return this._assetsService.addCategory(assetTypeId, body);
  }

  editCategory(id: number, body: BodyAddChecklistCategory): Observable<void> {
    return this._assetsService.editCategory(id, body);
  }

  deleteCategory(id: number): Observable<void> {
    return this._assetsService.deleteCategory(id);
  }

  // Category methods end

  addChecklistItem(
    id: number,
    body: BodyAddChecklistItem
  ): Observable<AddChecklistItemResponse> {
    const responseDto = this._assetsService.addChecklistItem(id, body);
    return responseDto.pipe(
      map(dto => this._assetsMapper.addChecklistItemToDomain(dto))
    );
  }

  getChecklistDetail(
    assetId: number,
    checklistId: number,
    params: CollectionAndReturnParams
  ): Observable<ChecklistDetail> {
    const checklistDetailDTO = this._assetsService.getChecklistDetail(
      assetId,
      checklistId,
      params
    );
    return checklistDetailDTO.pipe(
      map(dto => this._assetsMapper.assetItemChecklistDetailToDomain(dto))
    );
  }

  getCollectReturnDetail(
    assetId: number,
    collectReturnId: number
  ): Observable<CollectionReturnDetail> {
    const collectReturnDetailDTO = this._assetsService.getCollectReturnDetail(
      assetId,
      collectReturnId
    );
    return collectReturnDetailDTO.pipe(
      map(dto => this._assetsMapper.collectionAndReturnDetailToDomain(dto))
    );
  }

  updateChecklistItem(
    id: number,
    checklistId: number,
    body: BodyAddChecklistItem
  ): Observable<void> {
    return this._assetsService.updateChecklistItem(id, checklistId, body);
  }

  updateStatus(assetId: number, isActive: boolean): Observable<void> {
    return this._assetsService.updateStatus(assetId, isActive);
  }

  deleteChecklistItem(id: number): Observable<void> {
    return this._assetsService.deleteChecklistItem(id);
  }

  deleteAssetItem(id: number): Observable<void> {
    return this._assetsService.deleteAssetItem(id);
  }

  // Edit Asset type methods
  getDetailAssetType(assetTypeId: number): Observable<AssetDetail> {
    const assetDetailDTO = this._assetsService.getDetailAssetType(assetTypeId);
    return assetDetailDTO.pipe(
      map(dto => this._assetsMapper.assetTypeDetailToDomain(dto))
    );
  }

  updateAssetType(assetTypeId: number, body: BodyCreateAsset): Observable<any> {
    return this._assetsService.updateAssetType(assetTypeId, body);
  }

  // Edit Asset Item methods
  detailAssetItem(id: number): Observable<any> {
    return this._assetsService.detailAssetItem(id);
  }

  editAssetItem(id: number, body: BodyCreateAssetItem): Observable<any> {
    return this._assetsService.editAssetItem(id, body);
  }
}
